/**
 * Checkbox min/max, pane validation, and auto instruction copy for workflow panes.
 */

function tf(t, key, fallback) {
  if (typeof t !== "function") return fallback;
  const out = t(key);
  return out && String(out) !== key ? out : fallback;
}

export function getCheckboxMinMax(content) {
  const optionsLen = content?.options?.length ?? 0;
  if (optionsLen === 0) {
    return { minSelections: 0, maxSelections: 0, optionsLen: 0 };
  }
  const constraintsOff = content?.selectionConstraintsEnabled === false;
  const minRaw = constraintsOff ? 0 : content.minSelections ?? 0;
  const min = Math.max(0, Math.min(minRaw, optionsLen));
  let rawMax = content.maxSelections;
  if (constraintsOff) rawMax = null;
  const effectiveMaxRaw =
    rawMax == null || rawMax === 0
      ? optionsLen
      : Math.min(rawMax, optionsLen);
  const max = Math.max(min, effectiveMaxRaw);
  return { minSelections: min, maxSelections: max, optionsLen };
}

export function getCheckboxSelectedCount(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return 0;
  return Object.values(value).filter((v) => v === true).length;
}

export function isCheckboxMinSatisfied(content, value) {
  const { minSelections } = getCheckboxMinMax(content);
  return getCheckboxSelectedCount(value) >= minSelections;
}

/** True if any checkbox on the pane fails minimum selections. */
export function paneHasInvalidCheckboxMin(customItems, userData) {
  if (!Array.isArray(customItems)) return false;
  const data = userData && typeof userData === "object" ? userData : {};
  for (const item of customItems) {
    if (item?.type !== "checkbox") continue;
    const c = item.content;
    if (!c?.key) continue;
    if (!c?.options?.length) continue;
    if (!isCheckboxMinSatisfied(c, data[c.key])) return true;
  }
  return false;
}

/** Instruction line derived from constraints (same wording for blue and red states). */
export function getAutoCheckboxInstructionText(content, t) {
  const { minSelections, maxSelections, optionsLen } = getCheckboxMinMax(content);
  if (optionsLen === 0) return null;
  if (minSelections === maxSelections && minSelections > 0) {
    return minSelections === 1
      ? `${tf(t, "select_exactly", "Select exactly")} 1 ${tf(t, "option", "option")}`
      : `${tf(t, "select_exactly", "Select exactly")} ${minSelections} ${tf(t, "options", "options")}`;
  }
  if (minSelections > 0 && maxSelections < optionsLen) {
    return `${tf(t, "select", "Select")} ${minSelections}-${maxSelections} ${tf(t, "options", "options")}`;
  }
  if (minSelections > 0) {
    return minSelections === 1
      ? `${tf(t, "select_at_least", "Select at least")} 1 ${tf(t, "option", "option")}`
      : `${tf(t, "select_at_least", "Select at least")} ${minSelections} ${tf(t, "options", "options")}`;
  }
  if (maxSelections < optionsLen) {
    return maxSelections === 1
      ? `${tf(t, "select_up_to", "Select up to")} 1 ${tf(t, "option", "option")}`
      : `${tf(t, "select_up_to", "Select up to")} ${maxSelections} ${tf(t, "options", "options")}`;
  }
  return null;
}

/** When instruction is hidden, show this only after a failed advance. */
export function getCheckboxMinNotMetMessage(content, t) {
  const { minSelections } = getCheckboxMinMax(content);
  if (minSelections <= 0) return "";
  return minSelections === 1
    ? `${tf(t, "please_select_at_least", "Please select at least")} 1 ${tf(t, "option", "option")}`
    : `${tf(t, "please_select_at_least", "Please select at least")} ${minSelections} ${tf(t, "options", "options")}`;
}
